import { EngagementMetrics } from '../../../../hooks/useExecutiveData';

interface Props {
  engagement: EngagementMetrics;
  totalUsers: number;
}

const MetricRow = ({
  label, value, sub, pct, color = 'bg-violet-500',
}: { label: string; value: string | number; sub?: string; pct?: number; color?: string }) => (
  <div className="flex items-center gap-4 py-3 border-b border-white/5 last:border-0">
    <div className="flex-1 min-w-0">
      <p className="text-[13px] text-white font-500">{label}</p>
      {sub && <p className="text-[11px] text-white/40 mt-0.5">{sub}</p>}
    </div>
    <div className="text-right shrink-0">
      <p className="text-[16px] font-700 text-white">{value}</p>
    </div>
    {pct !== undefined && (
      <div className="w-24 shrink-0">
        <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${color} transition-all duration-500`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
        <p className="text-[10px] text-white/30 mt-1 text-right">{pct}%</p>
      </div>
    )}
  </div>
);

const BarChart = ({ data, max }: { data: { label: string; count: number }[]; max: number }) => (
  <div className="space-y-2.5">
    {data.map((item) => {
      const w = max > 0 ? Math.max((item.count / max) * 100, item.count > 0 ? 4 : 0) : 0;
      return (
        <div key={item.label} className="flex items-center gap-3">
          <span className="text-[12px] text-white/50 w-36 shrink-0 truncate">{item.label}</span>
          <div className="flex-1 h-5 bg-white/5 rounded-lg overflow-hidden">
            <div
              className="h-full bg-violet-500/60 rounded-lg transition-all duration-500 flex items-center pl-2"
              style={{ width: `${w}%` }}
            >
              {item.count > 0 && (
                <span className="text-[10px] font-700 text-white whitespace-nowrap">
                  {item.count}
                </span>
              )}
            </div>
          </div>
          {item.count === 0 && <span className="text-[11px] text-white/30">0</span>}
        </div>
      );
    })}
  </div>
);

export const EngagementSection = ({ engagement, totalUsers }: Props) => {
  const pctActive = totalUsers > 0
    ? Math.round((engagement.usersActiveInPeriod / totalUsers) * 100)
    : 0;
  const pctNoActivity = totalUsers > 0
    ? Math.round((engagement.usersWithNoRecentActivity / totalUsers) * 100)
    : 0;
  const pctGoals = totalUsers > 0
    ? Math.round((engagement.usersWithActiveGoals / totalUsers) * 100)
    : 0;
  const pctCal = totalUsers > 0
    ? Math.round((engagement.usersWithCalendarActive / totalUsers) * 100)
    : 0;

  const maxActivity = Math.max(...engagement.activityDistribution.map((d) => d.count), 1);
  const maxPlan = Math.max(...engagement.planDistribution.map((d) => d.count), 1);

  return (
    <div className="space-y-6">
      {/* Overview row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Ativos no período', value: engagement.usersActiveInPeriod, pct: pctActive, color: 'bg-emerald-500' },
          { label: 'Sem movimentação', value: engagement.usersWithNoRecentActivity, pct: pctNoActivity, color: 'bg-orange-500' },
          { label: 'Com metas ativas', value: engagement.usersWithActiveGoals, pct: pctGoals, color: 'bg-cyan-500' },
          { label: 'Com calendário', value: engagement.usersWithCalendarActive, pct: pctCal, color: 'bg-violet-500' },
        ].map((c) => (
          <div key={c.label} className="bg-white/5 border border-white/10 rounded-2xl p-4">
            <p className="text-[12px] text-white/40 mb-2">{c.label}</p>
            <p className="text-[24px] font-700 text-white">{c.value}</p>
            <div className="mt-2.5 w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${c.color}`} style={{ width: `${c.pct}%` }} />
            </div>
            <p className="text-[11px] text-white/30 mt-1">{c.pct}% da base</p>
          </div>
        ))}
      </div>

      {/* Médias */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <h3 className="text-[14px] font-600 text-white mb-4">Médias por usuário ativo</h3>
          <MetricRow label="Média de lançamentos" value={engagement.avgTransactionsPerUser.toFixed(1)} sub="por usuário com movimentação" />
          <MetricRow label="Média de contas" value={engagement.avgAccountsPerUser.toFixed(1)} sub="por usuário" />
          <MetricRow label="Média de cartões" value={engagement.avgCardsPerUser.toFixed(1)} sub="por usuário" />
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <h3 className="text-[14px] font-600 text-white mb-4">Distribuição por plano</h3>
          {engagement.planDistribution.length === 0 ? (
            <p className="text-[13px] text-white/30">Sem dados de plano disponíveis</p>
          ) : (
            <BarChart data={engagement.planDistribution.map((p) => ({ label: p.planName, count: p.count }))} max={maxPlan} />
          )}
        </div>
      </div>

      {/* Distribuição de engajamento */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <h3 className="text-[14px] font-600 text-white mb-5">Distribuição de atividade da base</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <BarChart data={engagement.activityDistribution} max={maxActivity} />
          <div className="grid grid-cols-2 gap-3">
            {engagement.activityDistribution.map((item) => {
              const pctItem = totalUsers > 0 ? Math.round((item.count / totalUsers) * 100) : 0;
              const colors = ['bg-emerald-500/20 text-emerald-400', 'bg-cyan-500/20 text-cyan-400', 'bg-amber-500/20 text-amber-400', 'bg-red-500/20 text-red-400'];
              const idx = engagement.activityDistribution.indexOf(item);
              return (
                <div key={item.label} className={`rounded-xl p-3 ${colors[idx % colors.length].split(' ')[0]} border border-white/5`}>
                  <p className={`text-[20px] font-700 ${colors[idx % colors.length].split(' ')[1]}`}>{pctItem}%</p>
                  <p className="text-[11px] text-white/50 mt-0.5 leading-tight">{item.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
